import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { conditionalDevtools } from '@/lib/storeMiddleware';
import type { ReplyContext } from '@/types/ui';
import type { Signature, Rule, Email } from '@/types/email';
import { mailEvents } from '@/events/mailEvents';
import type { SearchMode as SearchModeType } from '@/types/settings';
import { updateSearchModeSettings, updateLabelSettings } from '@/api/settings';
import { getErrorMessage } from '@/lib/error';
import { useCalendarModalStore } from './calendarModalStore';
import { useSearchStore } from './searchStore';

export type ViewMode = 'mailbox' | 'search';
export type Workspace = 'mail' | 'todo' | 'calendar' | 'notes';
/**
 * Sub-views of the mail workspace, switched by the tab strip in the list
 * header. 'mailbox' and 'briefing' render MailboxPage; the rest are full
 * pages that keep the same sidebar + main-panel shell.
 */
export type MailView = 'mailbox' | 'briefing' | 'triage' | 'surveys' | 'subscriptions';
export type PrimaryActionContext = 'compose' | 'task' | 'event' | 'note';
export type SettingsTab =
  | 'general'
  | 'contacts'
  | 'rules'
  | 'signatures'
  | 'accounts'
  | 'folders'
  | 'labels'
  | 'keyboard'
  | 'todo-tags';
export type SearchMode = SearchModeType;

interface UIState {
  activeWorkspace: Workspace;
  setActiveWorkspace: (workspace: Workspace) => void;
  mailView: MailView;
  setMailView: (view: MailView) => void;
  primaryActionContext: PrimaryActionContext;
  primaryActionSignal: number;
  triggerPrimaryAction: () => void;
  acknowledgePrimaryAction: () => void;
  view: ViewMode;
  setView: (view: ViewMode) => void;

  labelFilterByFolder: Record<string, string>;
  setLabelFilter: (accountId: string, folderId: string, labelId: string) => void;
  getLabelFilter: (accountId: string, folderId: string) => string;

  mobileMenuOpen: boolean;
  setMobileMenuOpen: (open: boolean) => void;

  // Mobile scope sheet (account / folder / layout picker) — replaces the
  // sidebar drawer on mail surfaces. Opened from the list header scope line.
  scopeSheetOpen: boolean;
  setScopeSheetOpen: (open: boolean) => void;

  // Number of mounted mobile reader action bars (email detail / thread /
  // digest). While > 0 the bottom navigation yields to the action bar.
  mobileReaderBarCount: number;
  adjustMobileReaderBarCount: (delta: number) => void;

  addAccountOpen: boolean;
  setAddAccountOpen: (open: boolean) => void;
  editAccountOpen: boolean;
  setEditAccountOpen: (open: boolean) => void;
  settingsDialogOpen: boolean;
  setSettingsDialogOpen: (open: boolean) => void;
  settingsInitialTab: SettingsTab;
  setSettingsInitialTab: (tab: SettingsTab) => void;
  composeOpen: boolean;
  setComposeOpen: (open: boolean) => void;
  draftEmail: Email | null;
  setDraftEmail: (email: Email | null) => void;
  signatureEditorOpen: boolean;
  setSignatureEditorOpen: (open: boolean) => void;
  ruleEditorOpen: boolean;
  setRuleEditorOpen: (open: boolean) => void;
  signatureEditorTarget: Signature | null;
  setSignatureEditorTarget: (signature: Signature | null) => void;
  replyContext: ReplyContext | null;
  setReplyContext: (context: ReplyContext | null) => void;
  pendingReply: { mode: 'reply' | 'replyAll' | 'forward'; emailId: string } | null;
  setPendingReply: (pending: { mode: 'reply' | 'replyAll' | 'forward'; emailId: string } | null) => void;
  globalError: string | null;
  setGlobalError: (message: string | null) => void;
  editingRule: Rule | null;
  setEditingRule: (rule: Rule | null) => void;
  ruleEditorAccountId: string | null;
  setRuleEditorAccountId: (accountId: string | null) => void;

  searchMode: SearchMode;
  setSearchMode: (mode: SearchMode) => void;
  hydrateSearchMode: (mode: SearchMode) => void;

  // Display-only feature flag: labels are still evaluated and applied
  // server-side while hidden; this only gates label UI.
  labelsVisible: boolean;
  setLabelsVisible: (visible: boolean) => void;
  hydrateLabelsVisible: (visible: boolean) => void;

  resetEmailSelection: () => void;
}

export const useUIStore = create<UIState>()(
  conditionalDevtools(
    persist(
      (set, get) => ({
        activeWorkspace: 'mail',
        mailView: 'mailbox',
        primaryActionContext: 'compose',
        primaryActionSignal: 0,
        view: 'mailbox',
        labelFilterByFolder: {},
        mobileMenuOpen: false,
        scopeSheetOpen: false,
        mobileReaderBarCount: 0,
        addAccountOpen: false,
        editAccountOpen: false,
        settingsDialogOpen: false,
        settingsInitialTab: 'general',
        composeOpen: false,
        draftEmail: null,
        signatureEditorOpen: false,
        ruleEditorOpen: false,
        signatureEditorTarget: null,
        replyContext: null,
        pendingReply: null,
        globalError: null,
        editingRule: null,
        ruleEditorAccountId: null,
        searchMode: 'hybrid',
        labelsVisible: true,

        setActiveWorkspace: (workspace) =>
          set((state) => {
            if (state.activeWorkspace === workspace) {
              return state;
            }
            // Clear search when switching workspaces
            useSearchStore.getState().clearSearch();
            return {
              activeWorkspace: workspace,
              primaryActionContext: resolvePrimaryAction(workspace),
            };
          }),

        setMailView: (view) =>
          set((state) => (state.mailView === view ? state : { mailView: view })),

        triggerPrimaryAction: () => {
          const context = get().primaryActionContext;
          if (context === 'compose') {
            set({ composeOpen: true });
            return;
          }
          if (context === 'event') {
            useCalendarModalStore.getState().openCreateDialog();
            return;
          }
          set((state) => ({
            primaryActionSignal: state.primaryActionSignal + 1,
          }));
        },

        acknowledgePrimaryAction: () => set({ primaryActionSignal: 0 }),
        setView: (view) => set({ view }),

        setLabelFilter: (accountId, folderId, labelId) => {
          const key = `${accountId}:${folderId}`;
          set((state) => ({
            labelFilterByFolder: {
              ...state.labelFilterByFolder,
              [key]: labelId,
            },
          }));
        },

        getLabelFilter: (accountId, folderId) => {
          const key = `${accountId}:${folderId}`;
          return get().labelFilterByFolder[key] ?? 'all';
        },

        setMobileMenuOpen: (open) => set({ mobileMenuOpen: open }),
        setScopeSheetOpen: (open) => set({ scopeSheetOpen: open }),
        adjustMobileReaderBarCount: (delta) =>
          set((state) => ({ mobileReaderBarCount: Math.max(0, state.mobileReaderBarCount + delta) })),
        setAddAccountOpen: (open) => set({ addAccountOpen: open }),
        setEditAccountOpen: (open) => set({ editAccountOpen: open }),
        setSettingsDialogOpen: (open) => set({ settingsDialogOpen: open }),
        setSettingsInitialTab: (tab) => set({ settingsInitialTab: tab }),
        setComposeOpen: (open) => set({ composeOpen: open }),
        setDraftEmail: (email) => set({ draftEmail: email }),
        setSignatureEditorOpen: (open) => set({ signatureEditorOpen: open }),
        setRuleEditorOpen: (open) => set({ ruleEditorOpen: open }),
        setSignatureEditorTarget: (signature) => set({ signatureEditorTarget: signature }),
        setReplyContext: (context) => set({ replyContext: context }),
        setPendingReply: (pending) => set({ pendingReply: pending }),
        setGlobalError: (message) => set({ globalError: message }),
        setEditingRule: (rule) => set({ editingRule: rule }),
        setRuleEditorAccountId: (accountId) => set({ ruleEditorAccountId: accountId }),

        setSearchMode: (mode) => {
          const previous = get().searchMode;
          if (previous === mode) {
            return;
          }
          set({ searchMode: mode });
          void persistSearchModePreference(mode, previous);
        },

        hydrateSearchMode: (mode) => {
          set({ searchMode: mode });
        },

        setLabelsVisible: (visible) => {
          const previous = get().labelsVisible;
          if (previous === visible) {
            return;
          }
          set({ labelsVisible: visible });
          void persistLabelsVisiblePreference(visible, previous);
        },

        hydrateLabelsVisible: (visible) => {
          set({ labelsVisible: visible });
        },

        resetEmailSelection: () => {
          // Use events to avoid circular dependencies
          mailEvents.emit('mail:selection:clear', undefined);
          set({ view: 'mailbox' });
        },
      }),
      {
        name: 'ui-storage',
        version: 4,
        partialize: (state) => ({
          // Don't persist filters - they should reset to 'all' on each session
          searchMode: state.searchMode,
          labelsVisible: state.labelsVisible,
          activeWorkspace: state.activeWorkspace,
          primaryActionContext: state.primaryActionContext,
        }),
        migrate: (persisted: unknown, version: number) => {
          const state = persisted as Record<string, unknown> | null;
          if (!state) return state;
          // v2 → v3: Orders stopped being a workspace and became a mail
          // sub-view; a persisted 'orders' workspace no longer parses.
          // v3 → v4: at the 2026-09-05 cutover Orders left sundae altogether
          // for the standalone ACOT Orders Tracker, so neither an 'orders'
          // workspace nor an 'orders' mailView parses any more. mailView was
          // never in partialize, but scrub it too rather than trust that a
          // build in between never persisted it.
          const next = { ...state };
          if (version < 4 && next.activeWorkspace === 'orders') next.activeWorkspace = 'mail';
          if (version < 4 && next.mailView === 'orders') next.mailView = 'mailbox';
          return next;
        },
      }
    ),
    { name: 'UIStore' }
  )
);

function resolvePrimaryAction(workspace: Workspace): PrimaryActionContext {
  if (workspace === 'todo') {
    return 'task';
  }
  if (workspace === 'calendar') {
    return 'event';
  }
  if (workspace === 'notes') {
    return 'note';
  }
  return 'compose';
}

async function persistSearchModePreference(nextMode: SearchMode, previousMode: SearchMode) {
  try {
    await updateSearchModeSettings(nextMode);
  } catch (error) {
    console.error('Failed to save search mode preference', error);
    const message = getErrorMessage(error) ?? 'Failed to save search mode preference';
    useUIStore.setState({ searchMode: previousMode, globalError: message });
  }
}

async function persistLabelsVisiblePreference(next: boolean, previous: boolean) {
  try {
    await updateLabelSettings(next);
  } catch (error) {
    console.error('Failed to save label visibility preference', error);
    const message = getErrorMessage(error) ?? 'Failed to save label visibility preference';
    useUIStore.setState({ labelsVisible: previous, globalError: message });
  }
}
